import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// React 19 native metadata
import { Search, ChevronRight, BookOpen } from 'lucide-react';
import { useBreadcrumbSchema } from '../common/useBreadcrumbSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GlossaryPage() {
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  // SEO: BreadcrumbList JSON-LD for hub indexing
  useBreadcrumbSchema('glossary-index', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Glossary', path: '/glossary' },
  ], []));

  useEffect(() => {
    fetch(`${API_URL}/api/glossary`)
      .then(r => r.json())
      .then(d => {
        setTerms(d.terms || []);
        setCategories(['All', ...(d.categories || [])]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = terms.filter(t => {
    const matchSearch = !search || t.term.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || t.category === activeCategory;
    return matchSearch && matchCat;
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "AI Marketing Glossary",
    "description": "Comprehensive glossary of AI marketing terms, strategies, and concepts. Learn about multi-agent AI, competitive intelligence, content multiplication, and more.",
    "url": "https://isupplyai.com/glossary",
    "publisher": { "@type": "Organization", "name": "iSupplyAI" },
    "hasDefinedTerm": terms.map(t => ({
      "@type": "DefinedTerm",
      "name": t.term,
      "url": `https://isupplyai.com/glossary/${t.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="glossary-page">
      <>
        <title>AI Marketing Glossary — Key Terms & Definitions | iSupplyAI</title>
        <meta name="description" content="Learn AI marketing terminology. Comprehensive glossary covering multi-agent AI, competitive intelligence, content strategy, and more." />
        <link rel="canonical" href="https://isupplyai.com/glossary" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="AI Marketing Glossary — Key Terms & Definitions" />
        <meta property="og:description" content="50 essential AI marketing terms. Learn about multi-agent AI, competitive intelligence, content strategy, and more." />
        <meta property="og:url" content="https://isupplyai.com/glossary" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="AI Marketing Glossary — Key Terms & Definitions" />
        <meta name="twitter:description" content="50 essential AI marketing terms and definitions." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </>

      {/* Breadcrumbs */}
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="glossary-breadcrumbs">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">Glossary</span>
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-5xl mx-auto px-4 pt-8 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="w-8 h-8 text-purple-400" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" data-testid="glossary-title">
            AI Marketing Glossary — <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">Key Terms & Definitions</span>
          </h1>
        </div>
        <p className="text-gray-400 text-lg max-w-2xl" data-testid="glossary-subtitle">
          {terms.length} essential terms and concepts in AI-powered marketing strategy. Learn what powers the future of marketing.
        </p>
      </header>

      {/* Filters */}
      <section className="max-w-5xl mx-auto px-4 mb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search terms..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500 transition-colors"
              data-testid="glossary-search"
            />
          </div>
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2" data-testid="glossary-categories">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'
                }`}
                data-testid={`glossary-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Terms List */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="glossary-empty">
            No terms match your search.
          </div>
        ) : (
          <div className="space-y-3" data-testid="glossary-list">
            {filtered.map(t => (
              <Link key={t.slug} to={`/glossary/${t.slug}`} className="group block p-5 rounded-xl bg-white/[0.02] border border-white/8 hover:border-purple-500/25 hover:bg-white/[0.04] transition-all" data-testid={`glossary-item-${t.slug}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-base font-semibold text-white group-hover:text-purple-300 transition-colors">{t.term}</h2>
                      <span className="px-2 py-0.5 rounded bg-white/5 text-xs text-gray-500 flex-shrink-0">{t.category}</span>
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed">{t.definition}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-purple-400 transition-colors flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
